import { z } from "zod"

import {
  organizationInvitationStatuses,
  organizationRoles,
} from "../../primitives.js"
import { ApiOrganizationMemberSchema, ApiOrganizationSchema } from "./organizations.js"
import { ApiUserSummarySchema } from "./users.js"

export const ApiOrganizationInvitationSchema = z.object({
  invitationId: z.string().uuid(),
  organizationId: z.string().uuid(),
  email: z.string(),
  role: z.enum(organizationRoles),
  status: z.enum(organizationInvitationStatuses),
  invitedByUserId: z.string(),
  // Mirrored profile of the inviter; null when no local row resolved.
  invitedBy: ApiUserSummarySchema.nullable().optional(),
  createdAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  acceptedAt: z.string().datetime().nullable(),
  acceptedByUserId: z.string().nullable(),
  acceptedBy: ApiUserSummarySchema.nullable().optional(),
  revokedAt: z.string().datetime().nullable(),
  lastSendAttemptAt: z.string().datetime().nullable(),
  lastSentAt: z.string().datetime().nullable(),
  deliveryStatus: z.enum(["pending", "sending", "sent", "failed"]),
})

export type ApiOrganizationInvitation = z.infer<
  typeof ApiOrganizationInvitationSchema
>

export const ApiCreateInvitationBodySchema = z.object({
  email: z.string().email().max(320),
  role: z.enum(organizationRoles).default("member"),
})

export type ApiCreateInvitationBody = z.infer<
  typeof ApiCreateInvitationBodySchema
>

export const ApiInvitationResponseSchema = z.object({
  invitation: ApiOrganizationInvitationSchema,
})

export type ApiInvitationResponse = z.infer<typeof ApiInvitationResponseSchema>

export const ApiInvitationListResponseSchema = z.object({
  invitations: z.array(ApiOrganizationInvitationSchema),
})

export type ApiInvitationListResponse = z.infer<
  typeof ApiInvitationListResponseSchema
>

export const apiPublicInvitationInvalidReasons = [
  "invalid",
  "expired",
  "revoked",
  "accepted",
] as const

export type ApiPublicInvitationInvalidReason =
  (typeof apiPublicInvitationInvalidReasons)[number]

/**
 * Public (unauthenticated) invite preview for the accept page. Invalid tokens
 * get a uniform response with null details so the endpoint never reveals
 * whether an organization exists behind a guessed token.
 */
export const ApiPublicInvitationResponseSchema = z.object({
  valid: z.boolean(),
  reason: z.enum(apiPublicInvitationInvalidReasons).optional(),
  organizationName: z.string().nullable(),
  /** Masked, e.g. j***@firm.com. */
  email: z.string().nullable(),
  role: z.enum(organizationRoles).nullable(),
})

export type ApiPublicInvitationResponse = z.infer<
  typeof ApiPublicInvitationResponseSchema
>

/** Org context after accepting, so the web app can switch into it. */
export const ApiAcceptInvitationResponseSchema = z.object({
  organization: ApiOrganizationSchema,
  member: ApiOrganizationMemberSchema,
})

export type ApiAcceptInvitationResponse = z.infer<
  typeof ApiAcceptInvitationResponseSchema
>
